import * as React from "react";

import type { GlideFunctionalRenderer } from "common/components/uis";
import { LOADING } from "common/components/computation-types";
import { loadedDefinedMap, asString, asBoolean } from "common/components/component-helpers";
import type { PalletteType } from "common/generator/components/color-types";
import { makeChartDataFromValues } from "./pie-chart-material-renderer";
import { lazilyLoadedComponent } from "lib/react-lazy-loading";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { ErrorBoundary } from "lib/error-boundary";

const StackedBarChart = lazilyLoadedComponent(
    "stacked-bar-chart",
    () => import("../ui/smart/charts/stacked-bar-chart/stacked-bar-chart")
);

export const StackedBarChartMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[list, titleValue, captionValue, showLegendValue, showLabelsValue, colorValue]] = useForceValueProducers(
        getValueProducers,
        force,
        ctx
    );
    if (list === LOADING) return env.renderProgress();
    if (list === undefined) return null;

    const title = loadedDefinedMap(titleValue, asString);
    const caption = loadedDefinedMap(captionValue, asString);
    const showLegendByDefault = loadedDefinedMap(showLegendValue, asBoolean);
    const showLabels = loadedDefinedMap(showLabelsValue, asBoolean);
    const pallette: PalletteType = loadedDefinedMap(colorValue, asString) as PalletteType;

    const data = makeChartDataFromValues(list, force);

    if (data.length === 0) return null;

    return (
        <ErrorBoundary>
            <React.Suspense fallback={<div style={{ width: 240, height: 240 }} />}>
                <StackedBarChart
                    title={title}
                    caption={caption}
                    pallette={pallette}
                    showLegendByDefault={showLegendByDefault}
                    showLabels={showLabels}
                    data={data}
                />
            </React.Suspense>
        </ErrorBoundary>
    );
};
